import {
	ALLOCATION_PRICING_METHOD,
	CARGO_TYPE,
	CARGO_TYPE_ID,
	FREIGHT_MODE,
	TEOSLoadSecurity,
	TShipmentOrder,
	TCarrierCostPayload,
} from '@types';
import {
	getTemperature,
	getTimeSlotStatus,
	getTruckSize,
	getTruckType,
	getPricingMethodFromCargoType,
	groupCargosByStop
} from '@utils';

const PRICING_METHOD_NAMES: Partial<Record<ALLOCATION_PRICING_METHOD, string>> = {
	[ALLOCATION_PRICING_METHOD.PALLETS]: 'Pallets',
	[ALLOCATION_PRICING_METHOD.FULL_LOAD]: 'FullLoad',
	[ALLOCATION_PRICING_METHOD.CPK]: 'CPK',
	[ALLOCATION_PRICING_METHOD.LOOSE]: 'Weight',
	[ALLOCATION_PRICING_METHOD.CUSTOM_ITEM]: 'Item',
};

const getCargoType = (cargoTypeId: number): CARGO_TYPE => {
	if (cargoTypeId === CARGO_TYPE_ID.FullTruckload) return CARGO_TYPE.FTL;
	if (cargoTypeId === CARGO_TYPE_ID.Loose) return CARGO_TYPE.LOOSE;
	if (cargoTypeId === CARGO_TYPE_ID.Other) return CARGO_TYPE.OTHER;
	if (cargoTypeId === CARGO_TYPE_ID.CustomItem) return CARGO_TYPE.ITEM;
	return CARGO_TYPE.PALLETS;
};

type TBuildCarrierCostPayloadOverrides = {
	freight_mode?: FREIGHT_MODE;
	truck_type_id?: number;
	truck_size_id?: number;
	pricing_method_id?: ALLOCATION_PRICING_METHOD;
};

export const buildCarrierCostPayload = (shipment: TShipmentOrder, overrides?: TBuildCarrierCostPayloadOverrides): TCarrierCostPayload => {
	const pricingMethodId = overrides?.pricing_method_id ?? getPricingMethodFromCargoType(shipment.cargo_type_id);
	const pricingMethod = pricingMethodId !== null ? (PRICING_METHOD_NAMES[pricingMethodId] ?? 'Pallets') : 'Pallets';

	const pickupStops: any[] = [];
	const dropoffStops: any[] = [];
	const cargos: any[] = [];

	const pickupGroups = groupCargosByStop(shipment.cargos).pickups;
	const pickupStopIndexByCargo: number[] = [];
	pickupGroups.forEach((group, groupIndex) => {
		group.forEach((cargoIndex) => pickupStopIndexByCargo[cargoIndex] = groupIndex);
	});

	shipment.cargos.forEach((cargo, i) => {
		if (pickupGroups[pickupStopIndexByCargo[i]][0] === i) pickupStops.push({
			action_type: 'PICKUP',
			location: { text: cargo.pickup.address, locality_type: cargo.pickup.locality_type ?? 'COMPLETE_ADDRESS' },
			modifiers: {
				PALLET_JACK: shipment.need_pallet_jack || shipment.pallet_handling.includes('pallet_jack_required'),
				PALLET_TRANSFER: shipment.pallet_handling.includes('pallet_transfer_required'),
				PALLET_EXCHANGE: shipment.pallet_handling.includes('pallet_exchange_required'),
				TAILGATE: cargo.pickup.requires_tailgate_lift,
				HAND_UNLOAD: cargo.pickup.requires_hand_unload,
			},
			truck_sizes_requiring_split: cargo.pickup.truck_sizes_requiring_split ?? [],
			cargo_index: i,
			action_on: typeof cargo.pickup_at === 'string' ? cargo.pickup_at : (cargo.pickup_at as Date).toISOString().split('T')[0],
			time_slot_status: getTimeSlotStatus(cargo.pickup),
		});

		if (dropoffStops.length === 0) {
			dropoffStops.push({
				action_type: 'DROPOFF',
				location: { text: cargo.dropoff.address, locality_type: cargo.dropoff.locality_type ?? 'COMPLETE_ADDRESS' },
				modifiers: {
					PALLET_JACK: shipment.need_pallet_jack || shipment.pallet_handling.includes('pallet_jack_required'),
					PALLET_TRANSFER: shipment.pallet_handling.includes('pallet_transfer_required'),
					PALLET_EXCHANGE: shipment.pallet_handling.includes('pallet_exchange_required'),
					TAILGATE: cargo.dropoff.requires_tailgate_lift,
					HAND_UNLOAD: cargo.dropoff.requires_hand_unload,
				},
				truck_sizes_requiring_split: cargo.dropoff.truck_sizes_requiring_split ?? [],
				cargo_index: i,
				action_on: typeof cargo.drop_at === 'string' ? cargo.drop_at : (cargo.drop_at as Date).toISOString().split('T')[0],
				time_slot_status: getTimeSlotStatus(cargo.dropoff),
			});
		}

		const cargoType = getCargoType(shipment.cargo_type_id);
		cargos.push({
			cargo_type: cargoType,
			pallet_quantity: (cargoType === CARGO_TYPE.LOOSE || cargoType === CARGO_TYPE.OTHER) ? null : (cargo.pallet_qty ?? 0),
			pallet_space_quantity: (cargoType === CARGO_TYPE.LOOSE || cargoType === CARGO_TYPE.OTHER || cargoType === CARGO_TYPE.ITEM) ? null : (cargo.qty ?? 0),
			total_weight_kg: Number(cargo.total_weight ?? 0),
			other_quantity: cargoType === CARGO_TYPE.OTHER ? (cargo.qty ?? 0) : undefined,
			temperature: getTemperature(shipment.temperature_id),
			contains_dangerous_goods: shipment.is_dangerous ?? false,
			dangerous_goods_data: {
				LOAD_CONTAINS_BULK_DANGEROUS_GOODS: shipment.is_dangerous ? shipment.dangerous_good_meta_data?.includes('Load contains bulk dangerous goods.') : false,
				LOAD_CONTAINS_FULLY_REGULATED_DANGEROUS_GOODS: shipment.is_dangerous ? shipment.dangerous_good_meta_data?.includes('Load contains fully regulated dangerous goods.') : false,
				LOAD_CONTAINS_LIMITED_QUANTITIES: shipment.is_dangerous ? shipment.dangerous_good_meta_data?.includes('Load contains limited quantities of dangerous goods.') : false,
			},
			height_cm: (cargoType === CARGO_TYPE.OTHER || cargoType === CARGO_TYPE.ITEM) ? cargo.height : undefined,
			length_cm: (cargoType === CARGO_TYPE.OTHER || cargoType === CARGO_TYPE.ITEM) ? cargo.length : undefined,
			width_cm: (cargoType === CARGO_TYPE.OTHER || cargoType === CARGO_TYPE.ITEM) ? cargo.width : undefined,
			overweight_details: cargoType === CARGO_TYPE.PALLETS ? cargo.overweight_pallets_information : undefined,
			...((cargo.cargo_item && cargoType === CARGO_TYPE.ITEM) ? {
				cargo_item: {
					item_id: cargo.cargo_item.item_id,
					name: cargo.cargo_item.name,
					weight_kg: cargo.cargo_item.weight_kg,
					description: cargo.cargo_item.description,
					charge_type: cargo.cargo_item.charge_type,
				}
			} : {}),
		});
	});

	const consolidatedStops = [...pickupStops, ...dropoffStops];
	const cargoStops: any[] = [];

	shipment.cargos.forEach((_, i) => {
		const dropoffIdx = consolidatedStops.findIndex((s) => s.action_type === 'DROPOFF');
		cargoStops.push({ stop_index: pickupStopIndexByCargo[i], cargo_index: i });
		cargoStops.push({ stop_index: dropoffIdx, cargo_index: i });
	});

	const stops = consolidatedStops.map(({ cargo_index: _ci, ...rest }) => rest);

	const loadSecurity = !Array.isArray(shipment.load_security) ? shipment.load_security as TEOSLoadSecurity : null;

	return {
		freight_mode: overrides?.freight_mode ?? shipment.freight_mode ?? FREIGHT_MODE.ROAD,
		truck_type: getTruckType(overrides?.truck_type_id ?? shipment.req_truck_type_id ?? 0),
		truck_size: getTruckSize(overrides?.truck_size_id ?? shipment.req_truck_size_id ?? 0) ?? null,
		stops,
		cargos,
		cargo_stops: cargoStops,
		pricing_method: pricingMethod,
		loading_options: {
			FROM_BACK: shipment.want_rear_load,
			FROM_SIDE: shipment.want_side_load,
			FROM_TOP: shipment.want_top_load,
		},
		load_restraints: loadSecurity ? {
			TENSION_BELTS: loadSecurity.tension_belt,
			EDGE_PROTECTION_SHIP_THIS_FREQUENTLY: loadSecurity.edge_protect,
			DRIVER_ASSISTANCE_AT_STOP: loadSecurity.driver_assist_stop,
			ANTI_SLIP_MAT: loadSecurity.anti_slip_mat,
			MULTILOCK: loadSecurity.multilock,
			IS_SIDEBOARDS_FIVE_PER_FIELD: loadSecurity.sideboards,
			CHAINS: loadSecurity.chains,
			STOP_BARS: loadSecurity.stopbars,
			CARGO_NET: loadSecurity.cargo_net,
			COIL_SUPPORTS: loadSecurity.coil,
			TIR_CABLE: loadSecurity.tir_cable,
			ANGLES_STRAPS_PLY_FOR_ALL_PALLETS: loadSecurity.asp_pallets,
			CORFLUTE: loadSecurity.corflute,
			GATES: loadSecurity.gates,
			LOADING_BARS: loadSecurity.loading_bars,
		} : {},
		shipper_external_id: shipment.shipper.external_id,
	};
};
